export type MoneyBag = {
  presentmentMoney: Money;
  shopMoney: Money;
};

export type Money = {
  amount: string;
  currencyCode: string;
};

export type MetaEvent = {
  timestamp: string;
  description: string;
  type: string;
};

export type AllocationLineItem = {
  id: string;
  referenceId: string;
  orderLineItemId?: string;
  events: MetaEvent[];
  info: MetaEvent[];
};

export type Allocation = {
  allocationId: string;
  fulfillmentOrderId: string;
  events: MetaEvent[];
  lineItems: AllocationLineItem[];
};

export enum PickUpStatus {
  NONE = "NONE",
  PENDING = "PENDING",
  READY_FOR_PICKUP = "READY_FOR_PICKUP",
  PICKED_UP = "PICKED_UP",
}

export type PickupAddress = Pick<
  Address,
  "address1" | "address2" | "city" | "province" | "phone" | "zip"
>;

export type Meta = {
  shipments: Allocation[];
  pickupStatus?: PickUpStatus;
  pickupAddress?: PickupAddress | null;
};

export type Image = {
  url: string;
  id: string;
};

export type ProductVariant = {
  id: string;
  image: Image;
  price: string;
  title: string;
};

export type LineItem = {
  id: string;
  title: string;
  currentQuantity: number;
  quantity: number;
  image: Image;
  variant: ProductVariant;
  discountedTotalSet: MoneyBag;
  originalUnitPriceSet: MoneyBag;
};

export type LineItemNode = {
  node: LineItem;
};

export type LineItemConnection = {
  edges: LineItemNode[];
  pageInfo: PageInfo;
};

export type FulfillmentOrderLineItem = {
  id: string;
  quantity: number;
  lineItem?: LineItem;
};

export enum FulfillmentOrderStatus {
  CANCELLED = "CANCELLED",
  CLOSED = "CLOSED",
  INCOMPLETE = "INCOMPLETE",
  IN_PROGRESS = "IN_PROGRESS",
  ON_HOLD = "ON_HOLD",
  OPEN = "OPEN",
  SCHEDULED = "SCHEDULED",
}

export enum FulfillmentDisplayStatus {
  ATTEMPTED_DELIVERY = "ATTEMPTED_DELIVERY",
  CANCELED = "CANCELED",
  CARRIER_PICKED_UP = "CARRIER_PICKED_UP",
  CONFIRMED = "CONFIRMED",
  DELAYED = "DELAYED",
  DELIVERED = "DELIVERED",
  FAILURE = "FAILURE",
  FULFILLED = "FULFILLED",
  IN_TRANSIT = "IN_TRANSIT",
  LABEL_PRINTED = "LABEL_PRINTED",
  LABEL_PURCHASED = "LABEL_PURCHASED",
  LABEL_VOIDED = "LABEL_VOIDED",
  MARKED_AS_FULFILLED = "MARKED_AS_FULFILLED",
  NOT_DELIVERED = "NOT_DELIVERED",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  PICKED_UP = "PICKED_UP",
  READY_FOR_PICKUP = "READY_FOR_PICKUP",
  SUBMITTED = "SUBMITTED",
}

export enum FulfillmentOrderRequestStatus {
  ACCEPTED = "ACCEPTED",
  CANCELLATION_ACCEPTED = "CANCELLATION_ACCEPTED",
  CANCELLATION_REJECTED = "CANCELLATION_REJECTED",
  CANCELLATION_REQUESTED = "CANCELLATION_REQUESTED",
  CLOSED = "CLOSED",
  REJECTED = "REJECTED",
  SUBMITTED = "SUBMITTED",
  UNSUBMITTED = "UNSUBMITTED",
}

export enum FulfillmentOrderAction {
  REQUEST_CANCELLATION = "REQUEST_CANCELLATION",
  REQUEST_FULFILLMENT = "REQUEST_FULFILLMENT",
}

export enum DeliveryMethodType {
  LOCAL = "LOCAL",
  NONE = "NONE",
  PICKUP_POINT = "PICKUP_POINT",
  PICK_UP = "PICK_UP",
  RETAIL = "RETAIL",
  SHIPPING = "SHIPPING",
}

export type ShopifyAdminFulfillmentOrder = {
  id: string;
  status: FulfillmentOrderStatus;
  requestStatus: FulfillmentOrderRequestStatus;
  supportedActions: {
    action: FulfillmentOrderAction;
  }[];
  assignedLocation: {
    name: string;
    address1: string;
    address2: string | null;
    city: string;
    province: string;
    phone: string | null;
    zip: string | null;
    location: {
      id: string;
    };
  };
  deliveryMethod: {
    methodType: DeliveryMethodType;
  };
  fulfillments: FulfillmentConnection;
  lineItems: {
    edges: {
      node: FulfillmentOrderLineItem;
    }[];
    pageInfo: PageInfo;
    nodes: FulfillmentOrderLineItem[];
  };
};

export type FulfillmentOrder = Omit<
  ShopifyAdminFulfillmentOrder,
  "fulfillments" | "lineItems"
> &
  Omit<Fulfillment, "id" | "fulfillmentLineItems"> & {
    lineItems: Partial<LineItem>[];
  };

export type FulfillmentOrderEdge = {
  node: ShopifyAdminFulfillmentOrder;
};

export type FulfillmentOrderConnection = {
  edges: FulfillmentOrderEdge[];
};

export type OrderTransaction = {
  id: string;
  gateway?: string;
  kind?: string;
  amountSet: MoneyBag;
  accountNumber?: string;
  paymentMethod?: string;
  paymentIcon?: PaymentIcon;
};

export type PaymentIcon = {
  altText: string;
  originalSrc: string;
  src: string;
  transformedSrc: string;
  url: string;
};

export type SuggestedOrderTransaction = {
  amountSet: MoneyBag;
  gateway: string;
  kind: "SUGGESTED_REFUND";
  parentTransaction: OrderTransaction;
};

export type SuggestedRefund = {
  suggestedTransactions: SuggestedOrderTransaction[];
  amountSet: MoneyBag;
  maximumRefundableSet: MoneyBag;
  shipping: {
    amountSet: MoneyBag;
    maximumRefundableSet: MoneyBag;
  };
};

export type Address = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  firstName: string;
  lastName: string;
  phone: string;
  province: string;
  zip: string;
};

export enum OrderDisplayFinancialStatus {
  AUTHORIZED = "AUTHORIZED",
  EXPIRED = "EXPIRED",
  PAID = "PAID",
  PARTIALLY_PAID = "PARTIALLY_PAID",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
  PENDING = "PENDING",
  REFUNDED = "REFUNDED",
  VOIDED = "VOIDED",
}

export type ShopifyAdminOrder = {
  id: string;
  name: string;
  createdAt: string;
  customer: Customer;
  processedAt: string;
  poNumber?: string;
  displayFinancialStatus: OrderDisplayFinancialStatus;
  orderNumber: number;
  transactions?: OrderTransaction[];
  metafield?: {
    value: string;
  };
  shippingAddress: Address;
  totalPriceSet: MoneyBag;
  subtotalPriceSet: MoneyBag;
  totalShippingPriceSet: MoneyBag;
  totalRefundedShippingSet: MoneyBag;
  currentTotalPriceSet: MoneyBag;
  currentTotalTaxSet: MoneyBag;
  currentSubtotalPriceSet: MoneyBag;
  totalTaxSet: MoneyBag;
  lineItems: LineItemConnection;
  fulfillmentOrders: FulfillmentOrderConnection;
  suggestedRefund?: SuggestedRefund;
  fulfillments: Fulfillment[];
};

export type Fulfillment = {
  id: string;
  name: string;
  createdAt: string;
  displayStatus: FulfillmentDisplayStatus;
  fulfillmentLineItems: FulfillmentLineItemConnection;
  trackingInfo: TrackingInfo[];
};

export type FulfillmentLineItem = {
  quantity: number;
  lineItem: Pick<LineItem, "id" | "quantity">;
};

export type FulfillmentLineItemEdge = {
  node: FulfillmentLineItem;
};

export type FulfillmentLineItemConnection = {
  edges: FulfillmentLineItemEdge[];
};

export type FulfillmentEdge = {
  node: Omit<Fulfillment, "fulfillmentLineItems"> & {
    fulfillmentLineItems: FulfillmentLineItemConnection;
  };
};

export type FulfillmentConnection = {
  edges: FulfillmentEdge[];
};

export type TrackingInfo = {
  company: string;
  number: string;
  url: string;
};

export type OrderResult = Omit<ShopifyAdminOrder, "metafield"> & {
  fulfillments: Fulfillment[];
  meta?: Meta;
  refundable?: boolean;
  currencyCode?: string;
};

export type FulfillmentSummary = Omit<Fulfillment, "fulfillmentLineItems"> & {
  fulfillmentLineItems: { id: string; quantity: number }[];
};

export type LineItemFulfillment = Omit<Fulfillment, "fulfillmentLineItems"> & {
  lineItems: LineItem[];
};

export type Order = Omit<OrderResult, "lineItems" | "fulfillments"> & {
  lineItems?: LineItem[];
  fulfillments: Fulfillment[];
  fulfilledLineItems: LineItemFulfillment[];
  unfulfilledLineItems: LineItem[];
};

export type OrderEdge = {
  node: ShopifyAdminOrder;
};

export type OrderConnection = {
  edges: OrderEdge[];
};

export type Customer = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orders: OrderConnection;
};

export type ShopifyResponse = {
  customer: Customer;
};

export type PageInfo = {
  hasNextPage: boolean;
  endCursor: string;
};

export type UserError = {
  field: string;
  message: string;
};

export type Refund = {
  id: string;
  note: string;
  totalRefundedSet: MoneyBag;
  shippingRefundSet: Money;
};

export type OrderSummary = {
  id: string;
  name: string;
  processedAt: string;
  poNumber: string;
  totalPriceSet: MoneyBag;
  lineItems: LineItem[];
  hasMoreLineItems: boolean;
  meta?: Meta;
};

export type OrderSummaryEdge = {
  cursor: string;
  node: OrderSummary;
};

export type OrderSummaryConnection = {
  edges: OrderSummaryEdge[];
};

export type OrderSummaryResponse = {
  customer: {
    id: string;
    orders: {
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string;
        endCurosr: string;
      };
      edges: {
        cursor: string;
        node: Pick<
          ShopifyAdminOrder,
          | "id"
          | "name"
          | "processedAt"
          | "totalPriceSet"
          | "lineItems"
          | "metafield"
          | "transactions"
          | "fulfillmentOrders"
          | "fulfillments"
        >;
      }[];
    };
  };
};
